// AusgeschiedenFelder.tsx — Eintritt und Austritt einer Verbindung Person ↔
// Unternehmen (User 28.09.2026).
//
// Letzte Zeile der Popups „Verantwortung fürs Unternehmen" / „Mensch im
// Unternehmen" (und „Verbindung zu Unternehmen"):
//   links  „Eintrittsdatum" (`beginn`, immer sichtbar)
//   rechts „Ist die Person ausgeschieden?" Ja/Nein
//   darunter rechts „Austrittsdatum" (`ende`) — nur bei Ja.
//
// Gespeichert an der Beziehung (ContactRelation.ausgeschieden/beginn/ende); die
// Liste der Karte streicht ausgeschiedene Einträge durch und nennt Ein-/Austritt
// als Hinweis.

import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { FieldRow, LabeledInput } from "../forms/formPrimitives";
import { REFINED } from "../risikoanalyseStep/tokens";
import type { AusgeschiedenFelder as Werte } from "./types";

const useStyles = makeStyles({
  wrap: { display: "flex", flexDirection: "column", rowGap: "10px" },
  frageBlock: { display: "flex", flexDirection: "column", rowGap: "7px", minWidth: 0 },
  frage: { fontFamily: REFINED.fontUI, fontSize: "0.86rem", fontWeight: 600, color: REFINED.ink },
  seg: { display: "inline-flex", columnGap: "6px" },
  segBtn: {
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("7px", "16px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.88rem",
    fontWeight: 600,
    color: REFINED.ink,
    backgroundColor: REFINED.white,
    cursor: "pointer",
  },
  segAktiv: { backgroundColor: REFINED.aub, color: REFINED.white, ...shorthands.borderColor(REFINED.aub) },
});

const de = (d?: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((d ?? "").trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
};

/**
 * Hinweis unter dem Namen in der Liste: „Eintritt 01.02.2020" bzw.
 * „ausgeschieden · Eintritt 01.02.2020 · Austritt 31.12.2024" (fehlende
 * Angaben entfallen). Leer, wenn es nichts zu sagen gibt.
 */
export function ausgeschiedenText(w: Werte): string {
  const teile: string[] = [];
  if (w.ausgeschieden) teile.push("ausgeschieden");
  const b = de(w.beginn);
  if (b) teile.push(`Eintritt ${b}`);
  const e = w.ausgeschieden ? de(w.ende) : "";
  if (e) teile.push(`Austritt ${e}`);
  return teile.join(" · ");
}

export function AusgeschiedenFelder({
  idBase,
  value,
  onChange,
}: {
  idBase: string;
  value: Werte;
  onChange: (patch: Werte) => void;
}) {
  const s = useStyles();
  const ja = !!value.ausgeschieden;
  return (
    <div className={s.wrap}>
      <FieldRow columns={2}>
        <LabeledInput
          label="Eintrittsdatum"
          name={`${idBase}-beginn`}
          type="date"
          value={value.beginn ?? ""}
          onChange={(v) => onChange({ beginn: v })}
        />
        <div className={s.frageBlock}>
          <span className={s.frage} id={`${idBase}-frage`}>
            Ist die Person ausgeschieden?
          </span>
          <div className={s.seg} role="group" aria-labelledby={`${idBase}-frage`}>
            {([true, false] as const).map((wert) => (
              <button
                key={String(wert)}
                type="button"
                aria-pressed={ja === wert}
                className={mergeClasses(s.segBtn, ja === wert && s.segAktiv)}
                onClick={() =>
                  // „Nein" räumt das Austrittsdatum mit weg — sonst stünde ein
                  // Ende an einer Verbindung, die als laufend gilt. Das
                  // Eintrittsdatum bleibt.
                  onChange(wert ? { ausgeschieden: true } : { ausgeschieden: false, ende: "" })
                }
              >
                {wert ? "Ja" : "Nein"}
              </button>
            ))}
          </div>
        </div>
      </FieldRow>
      {ja && (
        <FieldRow columns={2}>
          <span aria-hidden />
          <LabeledInput
            label="Austrittsdatum"
            name={`${idBase}-ende`}
            type="date"
            value={value.ende ?? ""}
            onChange={(v) => onChange({ ende: v })}
          />
        </FieldRow>
      )}
    </div>
  );
}
